import React, { useState } from 'react';
import NavBar from "../components/NavBar";
import Footer from '../components/Footer';
import { Helmet } from 'react-helmet-async';
import ValueMarquee from '../components/home/ValueMarquee';
import FeatureCards from '../components/home/FeatureCards';
import FeaturedBusinesses from '../components/home/FeaturedBusinesses';
import { useNavigate } from 'react-router-dom';
import { motion } from "framer-motion";

const titleVariant = {
    hidden: {
        opacity: 0,
        y: 10,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
        },
    },
};

const highlightVariant = {
    hidden: {
        opacity: 0,
        y: 8,
    },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            delay: 0.15,
            duration: 0.8,
            ease: [0.22, 1, 0.36, 1],
        },
    },
};

const NewHome = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-background min-h-full flex flex-col font-['Plus_Jakarta_Sans',sans-serif]">
            <Helmet>
                <meta name="description" content="Get the best of all the printing shops." />
                <meta property="og:title" content="Signet Print" />
                <meta property="og:description" content="Get the best of all the printing shops." />
                <meta property="og:image" content="/favicon-32x32.png" />
                <meta property="og:url" content="https://signetprint.co.za/" />
            </Helmet>
            <NavBar />
            <main className="flex-1 flex flex-col">
                <div className="bg-gradient-to-b from-white via-[#fff8f5] to-white relative overflow-hidden">
                    <div className="absolute top-[-200px] right-[-100px] w-[500px] h-[500px] bg-[#ff5323] opacity-[0.06] blur-[100px] rounded-full"></div>
                    <div className="absolute bottom-[-100px] left-[-100px] w-[400px] h-[400px] bg-[#ff8a65] opacity-[0.05] blur-[100px] rounded-full"></div>
                    <div className="absolute top-[50%] left-[50%] w-[300px] h-[300px] bg-[#fff0ec] opacity-[0.4] blur-[80px] rounded-full -translate-x-1/2 -translate-y-1/2"></div>

                    <div className="max-w-[1200px] mx-auto px-[16px] md:px-[40px] py-[64px] md:py-[96px] relative z-10">
                        <div className="max-w-[900px] mx-auto text-center">
                            <div className="flex items-start justify-center mb-[24px]">
                                <svg width="38" height="38" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 38C24.0391 38 28.8718 35.9982 32.435 32.435C35.9982 28.8718 38 24.0391 38 19C38 13.9609 35.9982 9.12818 32.435 5.56499C28.8718 2.0018 24.0391 1.75915e-05 19 1.66103e-05L19 19L19 38Z" fill="#FF5323" />
                                    <path d="M19 38C24.0391 38 28.8718 35.9982 32.435 32.435C35.9982 28.8718 38 24.0391 38 19C38 13.9609 35.9982 9.12818 32.435 5.56499C28.8718 2.0018 24.0391 1.75915e-05 19 1.66103e-05L19 19L19 38Z" fill="url(#paint0_linear_22_5890)" />
                                    <path d="M0 38C5.03911 38 9.87184 35.9982 13.435 32.435C16.9982 28.8718 19 24.0391 19 19C19 13.9609 16.9982 9.12816 13.435 5.56497C9.87184 2.00178 5.03912 9.81151e-07 4.52995e-06 0L0 38Z" fill="#FF5323" />
                                    <path d="M0 38C5.03911 38 9.87184 35.9982 13.435 32.435C16.9982 28.8718 19 24.0391 19 19C19 13.9609 16.9982 9.12816 13.435 5.56497C9.87184 2.00178 5.03912 9.81151e-07 4.52995e-06 0L0 38Z" fill="url(#paint1_linear_22_5890)" />
                                    <defs>
                                        <linearGradient id="paint0_linear_22_5890" x1="1.26666" y1="38" x2="28.4364" y2="-5.91666" gradientUnits="userSpaceOnUse">
                                            <stop stop-color="#E5762C" />
                                            <stop offset="1" stop-color="#B6CCF7" />
                                        </linearGradient>
                                        <linearGradient id="paint1_linear_22_5890" x1="1.26666" y1="38" x2="28.4364" y2="-5.91666" gradientUnits="userSpaceOnUse">
                                            <stop stop-color="#E5762C" />
                                            <stop offset="1" stop-color="#B6CCF7" />
                                        </linearGradient>
                                    </defs>
                                </svg>

                            </div>

                            <motion.h1
                                initial="hidden"
                                animate="visible"
                                className="font-semibold text-[42px] md:text-[64px] text-[#212326] leading-[1.1] tracking-[-2px] mb-[24px]"
                            >
                                <motion.div variants={titleVariant}>
                                    Print shops &amp; suppliers,
                                </motion.div>

                                <motion.div
                                    variants={highlightVariant}
                                    className="text-transparent bg-clip-text bg-gradient-to-r from-[#ff5323] to-[#ff8a65]"
                                >
                                    all in one place
                                </motion.div>
                            </motion.h1>
                            <motion.p
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    delay: 0.35,
                                    duration: 0.7,
                                    ease: [0.22, 1, 0.36, 1],
                                }}
                                className="font-medium text-[18px] md:text-[22px] text-[#231F20] leading-[1.6] mb-[40px] max-w-[700px] mx-auto"
                            >
                                Send files directly to print shops, discover trusted local suppliers,
                                and manage everything from one platform.
                            </motion.p>
                            <div className="flex flex-col sm:flex-row items-center justify-center gap-[12px] mb-[64px]">
                                <div className="relative group w-full sm:w-auto">
                                    <div className="absolute inset-0 bg-[#ff5323] rounded-[14px] blur-[20px] opacity-15 group-hover:opacity-25 transition-opacity">
                                    </div>
                                    <button
                                        className="relative flex items-center justify-center gap-[10px] px-[36px] py-[18px] rounded-[14px] bg-[#ff5323] text-white font-semibold text-[17px] hover:bg-[#e64a1f] transition-all shadow-[0px_2px_12px_rgba(255,83,35,0.15)] w-full sm:w-auto no-underline"
                                        onClick={() => navigate("/marketplace")}
                                        data-discover="true"
                                    >
                                        Browse Marketplace
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-right w-[20px] h-[20px]">
                                            <path d="M5 12h14"></path><path d="m12 5 7 7-7 7"></path>
                                        </svg>
                                    </button>
                                </div>
                                <button
                                    className="flex items-center justify-center gap-[10px] px-[36px] py-[18px] rounded-[14px] bg-white text-[#212326] font-semibold text-[17px] hover:bg-[#f8f8fa] transition-all border-2 border-[#e4e5e7] hover:border-[#ff5323] no-underline w-full sm:w-auto"
                                    onClick={() => navigate("/onboarding/plan")}
                                    data-discover="true"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-store w-[20px] h-[20px]">
                                        <path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"></path>
                                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"></path>
                                        <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"></path>
                                        <path d="M2 7h20"></path>
                                        <path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"></path>
                                    </svg>
                                    List Your Business
                                </button>
                            </div>
                            <ValueMarquee />
                        </div>
                    </div>

                </div>

                <FeatureCards />

                <FeaturedBusinesses />

            </main>
            <Footer />

        </div>

    );
};

export default NewHome;
